/**
 * UI → Agent synchronization (frontend half of the Frontend ↔ Agent Sync contract).
 *
 * Two responsibilities:
 *
 *  1. Send semantic `ui_event` frames over the shared Orbiee chat WebSocket
 *     whenever the shopper interacts with the UI (view changes, manual
 *     searches, product opens, cart/wishlist mutations, checkout steps).
 *     Wire shape:
 *       { type: "ui_event", seq, ts, event, data }
 *     `seq` is a per-connection monotonic integer (reset on every socket
 *     open); events are fire-and-forget and are NEVER queued while the
 *     socket is down — the `screen_context` snapshot on the next utterance
 *     is the documented resync mechanism.
 *
 *  2. Maintain a local screen-state mirror so every `start` / `voice`
 *     frame can carry a compact `screen_context` snapshot:
 *       { current_view, focused_product_id, focused_product_title,
 *         active_search_id, active_search_query, cart_badge_count, vto_open }
 *
 * Echo suppression: navigations / mutations caused by the agent's own
 * commands are tagged `source: "agent_navigation"` / `source: "agent"`
 * via `markAgentNavigation()` and `beginAgentAction()`/`endAgentAction()`,
 * so the backend reducer can exclude them from "recent manual actions".
 *
 * The view vocabulary is the closed enum from the contract:
 *   home | search_results | product_detail | cart | wishlist | checkout | vto
 * Routes outside the enum are mapped to the nearest view (see mapPathToView).
 *
 * This module is intentionally dependency-light (config only) so it can be
 * imported from contexts, pages and services without import cycles.
 */

import { UI_AGENT_SYNC_ENABLED } from "../config/apiConfig";

const MAX_RESULT_IDS = 20;
const MAX_QUERY_LENGTH = 200;
const AGENT_NAV_TTL_MS = 4000;
const STALE_EVENT_MS = 3000;
const CHECKOUT_STEP_DEDUPE_MS = 2000;

// ---------------------------------------------------------------------------
// Local screen-state mirror
// ---------------------------------------------------------------------------

const state = {
  routeView: "home",
  routeViewParams: null,
  previousRouteView: null,
  focusedProductId: null,
  focusedProductTitle: null,
  activeSearchId: null,
  activeSearchQuery: null,
  cartBadgeCount: 0,
  vtoOpen: false,
};

let socket = null;
let offSocketOpen = null;
let seq = 0;
let searchCounter = 0;
let lastViewSignature = null;
let firstViewTracked = false;
let agentNavMark = null; // { commandId: string|null, expiresAt: number }
let agentActionDepth = 0;
let lastCheckoutStep = null; // { step, at }

function currentView() {
  return state.vtoOpen ? "vto" : state.routeView;
}

function toIdString(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s ? s : null;
}

// ---------------------------------------------------------------------------
// Socket binding + low-level send
// ---------------------------------------------------------------------------

/**
 * Bind the shared OrbieeChatSocket instance used for `ui_event` frames.
 * Safe to call repeatedly with the same instance.
 */
export function bindUiSyncSocket(chatSocket) {
  if (!chatSocket || socket === chatSocket) return;
  unbindUiSyncSocket();
  socket = chatSocket;
  seq = 0;
  // seq is per-connection: reset whenever the underlying WS (re)opens.
  offSocketOpen = chatSocket.on("open", () => {
    seq = 0;
  });
}

/** Unbind (e.g. on logout / socket teardown). */
export function unbindUiSyncSocket() {
  if (offSocketOpen) {
    try {
      offSocketOpen();
    } catch {
      /* socket may already be destroyed */
    }
    offSocketOpen = null;
  }
  socket = null;
  seq = 0;
}

function sendUiEvent(event, data) {
  if (!UI_AGENT_SYNC_ENABLED) return false;
  if (!socket || !socket.isConnected) return false;
  seq += 1;
  try {
    return socket.sendUiEvent(seq, new Date().toISOString(), event, data);
  } catch (err) {
    console.warn("[uiAgentSync] failed to send ui_event", err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Agent echo-suppression markers
// ---------------------------------------------------------------------------

/**
 * Mark that the next navigation-like UI change (within a short window) was
 * caused by an agent command, so its effect events are tagged
 * `source: "agent_navigation"` instead of `manual_click`.
 */
export function markAgentNavigation(commandId = null) {
  agentNavMark = {
    commandId: commandId != null ? String(commandId) : null,
    expiresAt: Date.now() + AGENT_NAV_TTL_MS,
  };
}

/** Enter an agent-driven mutation section (cart/wishlist applied from agent responses). */
export function beginAgentAction() {
  agentActionDepth += 1;
}

/** Leave an agent-driven mutation section. Always pair with beginAgentAction (try/finally). */
export function endAgentAction() {
  agentActionDepth = Math.max(0, agentActionDepth - 1);
}

/** True while an agent-driven mutation section is active. */
export function isAgentActionActive() {
  return agentActionDepth > 0;
}

function resolveNavSource() {
  if (!firstViewTracked) return { source: "deeplink", commandId: null };
  if (agentNavMark && Date.now() < agentNavMark.expiresAt) {
    return { source: "agent_navigation", commandId: agentNavMark.commandId };
  }
  return { source: "manual_click", commandId: null };
}

// ---------------------------------------------------------------------------
// Route → view mapping (closed contract enum)
// ---------------------------------------------------------------------------

/**
 * Map an app route to the contract's closed view enum. Routes without an
 * exact equivalent map to the nearest view (product-owner decision):
 *   /recommendations → search_results, category/browse/account → home,
 *   /order-confirmation → checkout.
 */
export function mapPathToView(pathname) {
  const path = String(pathname || "/");
  if (path === "/" || path === "") return { view: "home", viewParams: null };
  if (path.startsWith("/search")) return { view: "search_results", viewParams: null };
  if (path.startsWith("/recommendations")) {
    return { view: "search_results", viewParams: null };
  }
  if (path.startsWith("/product/") || path.startsWith("/item/")) {
    const id = decodeURIComponent(path.split("/").filter(Boolean).pop() || "");
    return {
      view: "product_detail",
      viewParams: id ? { product_id: id } : null,
    };
  }
  if (path.startsWith("/cart")) return { view: "cart", viewParams: null };
  if (path.startsWith("/wishlist")) return { view: "wishlist", viewParams: null };
  if (path.startsWith("/checkout")) return { view: "checkout", viewParams: null };
  if (path.startsWith("/order-confirmation")) {
    return { view: "checkout", viewParams: { section: "order_confirmation" } };
  }
  return { view: "home", viewParams: null };
}

// ---------------------------------------------------------------------------
// Event trackers (semantic vocabulary)
// ---------------------------------------------------------------------------

/**
 * Route/view change. Called by the global route tracker; deduped on
 * identical consecutive view+params (StrictMode/re-render safe).
 */
export function trackViewChanged(view, viewParams = null) {
  if (!UI_AGENT_SYNC_ENABLED) return;
  if (!view) return;

  const params =
    viewParams && typeof viewParams === "object" && Object.keys(viewParams).length
      ? viewParams
      : null;
  const signature = `${view}|${JSON.stringify(params)}`;
  if (signature === lastViewSignature) return;
  lastViewSignature = signature;

  const { source, commandId } = resolveNavSource();
  firstViewTracked = true;

  state.previousRouteView = state.routeView;
  state.routeView = view;
  state.routeViewParams = params;

  if (view === "product_detail") {
    const pid = toIdString(params?.product_id);
    if (pid && pid !== state.focusedProductId) {
      state.focusedProductId = pid;
      state.focusedProductTitle = null; // trackProductViewed fills the title
    }
  } else {
    state.focusedProductId = null;
    state.focusedProductTitle = null;
  }

  sendUiEvent("view_changed", {
    view,
    view_params: params || {},
    source,
    command_id: commandId,
  });
}

/**
 * Manual site search completed. Generates and returns the client search_id.
 * IDs only (top N) — never card payloads.
 */
export function trackSearchPerformed({
  query,
  resultProductIds = [],
  resultCount = null,
  filters = null,
} = {}) {
  if (!UI_AGENT_SYNC_ENABLED) return null;
  const q = String(query ?? "").trim();
  if (!q) return null;

  searchCounter += 1;
  const searchId = `s-${Date.now().toString(36)}-${searchCounter}`;
  state.activeSearchId = searchId;
  state.activeSearchQuery = q;

  const ids = (Array.isArray(resultProductIds) ? resultProductIds : [])
    .map(toIdString)
    .filter(Boolean)
    .slice(0, MAX_RESULT_IDS);

  sendUiEvent("search_performed", {
    search_id: searchId,
    query: q.slice(0, MAX_QUERY_LENGTH),
    result_product_ids: ids,
    filters: filters && typeof filters === "object" ? filters : {},
    result_count: typeof resultCount === "number" ? resultCount : ids.length,
    source: "manual",
  });
  return searchId;
}

/**
 * Product detail opened (called by the product pages once per product).
 * Pass `fromView` explicitly for in-page switches (e.g. variant selection on
 * an already-open product page → fromView: "product_detail").
 */
export function trackProductViewed({
  productId,
  productTitle = null,
  fromView = null,
} = {}) {
  if (!UI_AGENT_SYNC_ENABLED) return;
  const pid = toIdString(productId);
  if (!pid) return;

  // The route tracker may already have switched routeView to product_detail
  // (product data often loads after the route effect) — fall back to the
  // previous view so from_view reports where the shopper actually came from.
  const resolvedFromView =
    fromView ||
    (state.routeView === "product_detail" && state.previousRouteView
      ? state.previousRouteView
      : state.routeView);

  const { source } = resolveNavSource();

  state.focusedProductId = pid;
  state.focusedProductTitle = productTitle != null ? String(productTitle) : null;

  const data = {
    product_id: pid,
    product_title: state.focusedProductTitle,
    source,
    from_view: resolvedFromView,
  };
  if (resolvedFromView === "search_results" && state.activeSearchId) {
    data.search_id = state.activeSearchId;
  }
  sendUiEvent("product_viewed", data);
}

/**
 * Cart mutated. action: add | remove | update_qty | clear.
 * `source` is captured at mutation time by the caller (agent vs manual).
 */
export function trackCartChanged({
  action,
  productId = null,
  productTitle = null,
  quantity = null,
  cartBadgeCount = null,
  source = null,
} = {}) {
  if (!UI_AGENT_SYNC_ENABLED) return;
  if (!["add", "remove", "update_qty", "clear"].includes(action)) return;

  if (typeof cartBadgeCount === "number" && cartBadgeCount >= 0) {
    state.cartBadgeCount = cartBadgeCount;
  }

  sendUiEvent("cart_changed", {
    action,
    product_id: toIdString(productId),
    product_title: productTitle != null ? String(productTitle) : null,
    quantity: typeof quantity === "number" ? quantity : null,
    cart_badge_count: state.cartBadgeCount,
    source: source || (isAgentActionActive() ? "agent" : "manual"),
  });
}

/** Wishlist mutated. action: add | remove. */
export function trackWishlistChanged({
  action,
  productId = null,
  productTitle = null,
  source = null,
} = {}) {
  if (!UI_AGENT_SYNC_ENABLED) return;
  if (!["add", "remove"].includes(action)) return;

  sendUiEvent("wishlist_changed", {
    action,
    product_id: toIdString(productId),
    product_title: productTitle != null ? String(productTitle) : null,
    source: source || (isAgentActionActive() ? "agent" : "manual"),
  });
}

/** Manual checkout progression (deduped per step within a short window). */
export function trackCheckoutStep(step) {
  if (!UI_AGENT_SYNC_ENABLED) return;
  const s = String(step ?? "").trim();
  if (!s) return;

  const now = Date.now();
  if (
    lastCheckoutStep &&
    lastCheckoutStep.step === s &&
    now - lastCheckoutStep.at < CHECKOUT_STEP_DEDUPE_MS
  ) {
    return;
  }
  lastCheckoutStep = { step: s, at: now };

  sendUiEvent("checkout_step", {
    step: s,
    source: isAgentActionActive() ? "agent" : "manual",
  });
}

/**
 * Virtual try-on overlay opened/closed. Reported as view_changed(vto) on
 * open and a view_changed back to the underlying route view on close.
 */
export function trackVtoChanged(open, productId = null) {
  if (!UI_AGENT_SYNC_ENABLED) return;
  const next = !!open;
  if (state.vtoOpen === next) return;
  state.vtoOpen = next;

  const { source, commandId } = resolveNavSource();
  if (next) {
    const pid = toIdString(productId);
    sendUiEvent("view_changed", {
      view: "vto",
      view_params: pid ? { product_id: pid } : {},
      source,
      command_id: commandId,
    });
  } else {
    sendUiEvent("view_changed", {
      view: state.routeView,
      view_params: state.routeViewParams || {},
      source,
      command_id: commandId,
    });
  }
}

/** Silent cart-count mirror update (initial load / snapshot replaces). */
export function setCartBadgeCount(count) {
  if (typeof count === "number" && count >= 0) {
    state.cartBadgeCount = count;
  }
}

// ---------------------------------------------------------------------------
// screen_context snapshot for start / voice frames
// ---------------------------------------------------------------------------

/** Compact snapshot attached to every `start`/`voice` frame. Null when disabled. */
export function getScreenContext() {
  if (!UI_AGENT_SYNC_ENABLED) return null;
  return {
    current_view: currentView(),
    focused_product_id: state.focusedProductId,
    focused_product_title: state.focusedProductTitle,
    active_search_id: state.activeSearchId,
    active_search_query: state.activeSearchQuery,
    cart_badge_count: state.cartBadgeCount,
    vto_open: state.vtoOpen,
  };
}

export { STALE_EVENT_MS as UI_EVENT_STALE_MS };
